import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { assetDetails } from 'store/asset';
import { Icon } from '@iconify/react';
import { getImage } from 'hooks/get-image';
import cn from 'classnames';
import Button from 'components/ui/Button';
import { format } from 'date-fns';

const ReturnAsset = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selected } = useSelector(state => state.asset);
  const [loading, setLoading] = useState(true);

  const { id } = useParams();

  const now = new Date();
  const date = format(now, 'dd/MM/yyyy');
  const hour = format(now, 'HH:mm');

  const [dateEstimed, hourEstimed] = selected ? selected?.estimated.split(' ') : '01/01/1970 00:00';

  const days = selected?.timer ? Math.floor(selected.timer / 24) : 0;

  const items = [
    {
      icon: 'lucide:calendar-arrow-up',
      title: t('asset.pickup'),
      text: date,
      note: hour
    },
    {
      icon: 'lucide:calendar-arrow-down',
      title: t('asset.deposit'),
      text: dateEstimed,
      note: hourEstimed
    },
    {
      icon: 'lucide:calendar-clock',
      title: t('asset.duration'),
      text: days + ' ' + t('general.day', { count: days }),
      note: selected?.timer + ' ' + t('general.hour', { count: 2 })
    },
    {
      icon: 'lucide:lock',
      title: t('locker.label'),
      text: t('general.number') + ' ' + selected?.locker?.doorNumber,
      note: t('locker.size', { size: selected?.locker?.size })
    }
  ]

  const fetchDetails = () => {
    setLoading(true);
    dispatch(assetDetails(id))
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      })
  }

  useEffect(() => {
    fetchDetails();
  }, [])

  const returnable = [5, 6];

  if (loading || !selected) {
    return (
      <div className="px-8 flex flex-col gap-8 h-full overflow-y-scroll pb-6 animate-pulse">
        <div className="h-6 bg-gray-300 rounded w-1/3" />
        <div className="h-5 bg-gray-300 rounded w-2/3" />
        <div className="h-44 bg-gray-300 rounded mx-auto w-44" />
        <div className="space-y-4">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="flex justify-between items-center border-b border-gray-200 pb-4"
            >
              <div className="flex items-center gap-3 w-1/3 h-5 bg-gray-300 rounded" />
              <div className="flex gap-2 w-1/2 h-4 bg-gray-300 rounded" />
            </div>
          ))}
        </div>
        <div className="h-12 bg-gray-300 rounded w-full" />
      </div>
    );
  }

  if(!returnable.includes(selected.state)){
    navigate('/home')
  }

  return (
    selected &&
    <div className="h-full w-full px-8 flex flex-col gap-5">
      <div>
        <span className="text-lg font-semibold">
          {t('user.asset.label')}
        </span>
      </div>
      <div className="flex justify-center items-center">
        <img
          alt={selected.id}
          src={getImage(selected.files)}
          className="h-40"
        />
      </div>
      <div className="h-full flex flex-col gap-3">
        {items.map((item, index) =>
          <div
            key={index}
            className={cn(
              'flex justify-between items-center',
              {
                'border-b border-[#e7e7e7] pb-5': index !== items.length - 1
              }
            )}
          >
            <div className="flex items-center gap-3">
              <Icon icon={item.icon} width="25" />
              <span className="font-semibold text-base">
                {item.title}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">
                {item.text}
              </span>
              <span className="text-[#808080] text-xs">
                ({item.note})
              </span>
            </div>
          </div>
        )}
      </div>
      <div>
        {returnable.includes(selected.state) &&
          <Button
            onClick={() => navigate('/asset/review/' + selected.id)}
            color="dark"
            outlined
            size="lg"
            text={t('asset.return')}
          />
        }
      </div>
    </div>
  )
}

export default ReturnAsset;